import { useEffect, useState } from "react";
import { useBlocker } from "@tanstack/react-router";
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  DownloadIcon,
  FileCheck2Icon,
  FolderIcon,
  UploadIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { base64ToBytes } from "../../../state/imanage";
import { downloadName } from "../imanage/imanageFormat";
import { confirmIManageSave, prepareIManageSave, saveName } from "../imanage/imanageSave";
import {
  openReviewDocument,
  reviewChanges,
  saveReviewedDocument,
  type ReviewDocument,
} from "./imanageDocx";

/** Paragraphs shown at once; long documents page. */
const PAGE = 40;

/**
 * Tracked-edit review of one Word document, with an explicit two-step save:
 * prepare (an approval for these exact bytes) and confirm (the only write).
 */
export function IManageDocumentReview({
  organizationId,
  libraryId,
  document,
  folder,
  canUpload,
  canSaveVersion,
  onPickFolder,
  onClose,
  onSaved,
}: {
  organizationId: string;
  libraryId: string;
  document: IManageDocument;
  folder: { id: string; name: string } | null;
  canUpload: boolean;
  canSaveVersion: boolean;
  onPickFolder: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [doc, setDoc] = useState<ReviewDocument | null>(null);
  const [edits, setEdits] = useState<ReadonlyMap<number, string>>(new Map());
  const [replacement, setReplacement] = useState<{ bytes: Uint8Array; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [mode, setMode] = useState<"version" | "document">(
    canSaveVersion && document.revision ? "version" : "document",
  );
  const [name, setName] = useState(saveName(document.name));
  const [prepared, setPrepared] = useState<{
    id: string;
    content: string;
    hash: string;
    mode: string;
    name: string;
    folder: string;
  } | null>(null);
  const [receipt, setReceipt] = useState<IManageEntry | null>(null);
  useEffect(() => {
    let active = true;
    void openReviewDocument(base64ToBytes(document.content)).then(
      (x) => {
        if (active) setDoc(x);
      },
      (e) => {
        if (active) setError(e.message);
      },
    );
    return () => {
      active = false;
    };
  }, [document.content]);
  const changes = doc ? reviewChanges(doc, edits) : [];
  const hasChanges = changes.length > 0 || replacement !== null;
  const download = () => {
    const url = URL.createObjectURL(new Blob([base64ToBytes(document.content).slice()]));
    const a = window.document.createElement("a");
    a.href = url;
    a.download = downloadName(document);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const prepare = async () => {
    setBusy(true);
    setError(null);
    try {
      const bytes =
        replacement?.bytes ?? (doc ? saveReviewedDocument(doc, edits, "Amoreg reviewer") : null);
      if (!bytes) throw new Error("Upload a reviewed Word file first.");
      const result = await prepareIManageSave(
        organizationId,
        {
          libraryId,
          documentId: document.id,
          folderId: folder?.id ?? "",
          mode,
          name,
          revision: document.revision,
        },
        bytes,
      );
      setPrepared({
        id: result.id,
        content: result.content,
        hash: result.hash,
        mode,
        name,
        folder: folder?.name ?? "",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not prepare the save.");
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!prepared) return;
    setBusy(true);
    setError(null);
    try {
      const result = await confirmIManageSave(organizationId, prepared);
      setReceipt(result);
      setPrepared(null);
      onSaved();
    } catch (e) {
      setError(
        `${e instanceof Error ? e.message : "Save outcome unknown. Check iManage before retrying."} Reference: ${prepared.id}`,
      );
      setPrepared(null);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (!hasChanges || receipt || window.confirm("Discard your unsaved document edits?")) onClose();
  };
  useBlocker({
    shouldBlockFn: () =>
      busy || (hasChanges && !receipt && !window.confirm("Discard your unsaved document edits?")),
    enableBeforeUnload: busy || (hasChanges && !receipt),
  });

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      if (file.size > 12 * 1024 * 1024) throw new Error("The file exceeds 12 MB.");
      const bytes = new Uint8Array(await file.arrayBuffer());
      await openReviewDocument(bytes);
      setReplacement({ bytes, name: file.name });
    } catch (error) {
      setError(error instanceof Error ? error.message : "Invalid Word file.");
    }
  };
  const pages = doc ? Math.ceil(doc.paragraphs.length / PAGE) : 0;

  return (
    <section className="flex min-h-0 flex-1 flex-col" aria-label="Document review" data-imanage-review="">
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border/60 px-5 py-2.5">
        <Button variant="ghost" size="xs" disabled={busy} onClick={close}>
          <ArrowLeftIcon aria-hidden />
          Close review
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[13px] font-semibold">{document.name}</h2>
          <p className="truncate text-xs text-muted-foreground">
            Version {document.version ?? "—"} · <span className="font-mono">{document.id}</span>
          </p>
        </div>
        <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
          {replacement
            ? "Reviewed file ready"
            : `${changes.length} ${changes.length === 1 ? "paragraph" : "paragraphs"} edited`}
        </span>
        <Button variant="outline" size="xs" onClick={download}>
          <DownloadIcon aria-hidden />
          Download original
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-6 py-6">
          {error && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-destructive/30 bg-destructive/[0.04] px-3 py-2 text-[13px] text-destructive"
            >
              {error}
            </p>
          )}
          {receipt ? (
            <p
              role="status"
              className="flex items-start gap-2.5 rounded-lg border border-success/40 bg-success/5 p-4 text-sm"
            >
              <CheckCircle2Icon aria-hidden className="mt-0.5 size-4 text-success" />
              <span>
                Saved {receipt.name} · version {receipt.version ?? "—"} ·{" "}
                <span className="font-mono text-xs">{receipt.id}</span>
              </span>
            </p>
          ) : prepared ? (
            <div
              className="space-y-4 rounded-xl border border-border p-5"
              role="region"
              aria-label="Confirm save"
            >
              <h3 className="text-sm font-semibold">Confirm save to iManage</h3>
              <p className="text-sm">
                Save “{prepared.name}” as a{" "}
                {prepared.mode === "version"
                  ? "new version of this document"
                  : `new document in ${prepared.folder}`}
                ?
              </p>
              <p className="text-[13px] text-muted-foreground">
                Only the file you just reviewed will be sent. Your original version is retained.
              </p>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">File fingerprint</summary>
                <code className="break-all">{prepared.hash}</code>
              </details>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => void save()}>
                  {busy ? "Saving…" : "Confirm and save"}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPrepared(null)}>
                  Back to review
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-5 flex flex-wrap items-center gap-3 text-[13px] text-muted-foreground">
                <p className="min-w-0 flex-1 text-pretty">
                  Edit a paragraph to record a Word tracked change. Paragraphs with fields, links,
                  comments or earlier revisions stay read-only; for those, edit in Word and upload
                  the file.
                </p>
                <label className="cursor-pointer">
                  <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-input px-2.5 text-xs text-foreground transition-colors hover:bg-accent">
                    <UploadIcon aria-hidden className="size-3.5" />
                    Upload reviewed file
                  </span>
                  <input
                    type="file"
                    accept=".docx"
                    className="sr-only"
                    disabled={busy}
                    onChange={(e) => {
                      void upload(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              {replacement ? (
                <div className="flex items-center gap-3 rounded-lg border border-border/70 px-4 py-3 text-sm">
                  <FileCheck2Icon aria-hidden className="size-4 text-success" />
                  <span className="min-w-0 flex-1 truncate">Using {replacement.name}</span>
                  <Button variant="ghost" size="xs" onClick={() => setReplacement(null)}>
                    Use editor instead
                  </Button>
                </div>
              ) : doc ? (
                <article className="rounded-xl border border-border/60 bg-card/40 px-2 py-3">
                  {doc.paragraphs.slice(page * PAGE, (page + 1) * PAGE).map((p) => {
                    const edited = edits.has(p.index) && edits.get(p.index) !== p.text;
                    return (
                      <div
                        key={p.index}
                        className={cn(
                          "rounded-md border-l-2 border-transparent px-4 py-1.5",
                          edited && "border-info bg-info/[0.05]",
                        )}
                      >
                        <label className="sr-only" htmlFor={`imanage-paragraph-${p.index}`}>
                          Paragraph {p.index + 1}
                          {!p.editable ? " · read-only" : ""}
                        </label>
                        {edited && (
                          <del className="block whitespace-pre-wrap text-[13.5px] leading-relaxed text-destructive/80">
                            {p.text}
                          </del>
                        )}
                        {p.editable ? (
                          <textarea
                            disabled={busy}
                            id={`imanage-paragraph-${p.index}`}
                            rows={1}
                            value={edits.get(p.index) ?? p.text}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEdits((current) => new Map(current).set(p.index, value));
                            }}
                            className={cn(
                              "block w-full resize-none bg-transparent text-[13.5px] leading-relaxed outline-none [field-sizing:content]",
                              "rounded-sm focus-visible:ring-2 focus-visible:ring-ring/40",
                              edited && "text-foreground",
                            )}
                          />
                        ) : (
                          <p
                            id={`imanage-paragraph-${p.index}`}
                            className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-muted-foreground"
                            title="Read-only here: edit in Word and upload the file."
                          >
                            {p.text || "Non-text content; preserved in the Word file."}
                          </p>
                        )}
                        {edits.has(p.index) && (
                          <Button
                            variant="ghost-muted"
                            size="micro"
                            className="mt-1 ms-auto flex"
                            onClick={() =>
                              setEdits((current) => {
                                const next = new Map(current);
                                next.delete(p.index);
                                return next;
                              })
                            }
                          >
                            Revert paragraph
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </article>
              ) : null}
              {doc && pages > 1 && !replacement && (
                <div className="mt-4 flex items-center justify-center gap-3 text-[13px]">
                  <Button variant="outline" size="xs" disabled={page === 0} onClick={() => setPage((x) => x - 1)}>
                    Previous paragraphs
                  </Button>
                  <span className="tabular-nums text-muted-foreground">
                    {page + 1} / {pages}
                  </span>
                  <Button
                    variant="outline"
                    size="xs"
                    disabled={page + 1 >= pages}
                    onClick={() => setPage((x) => x + 1)}
                  >
                    Next paragraphs
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!receipt && !prepared ? (
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-t border-border/60 bg-background px-5 py-3">
          <label className="flex w-52 flex-col gap-1 text-xs text-muted-foreground">
            Save as
            <select
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-[13px] text-foreground"
              value={mode}
              disabled={busy}
              onChange={(e) => setMode(e.target.value as "version" | "document")}
            >
              <option value="version" disabled={!canSaveVersion || !document.revision}>
                New version{!canSaveVersion ? " (awaiting tenant verification)" : ""}
              </option>
              <option value="document">New document</option>
            </select>
          </label>
          <label className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-muted-foreground">
            Document name
            <Input
              disabled={busy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="h-8 text-[13px]"
            />
          </label>
          {mode === "document" && (
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span>Folder</span>
              <Button variant="outline" size="sm" onClick={onPickFolder} className="h-8 max-w-48 justify-start sm:h-8">
                <FolderIcon aria-hidden />
                {folder?.name ?? "Choose folder"}
              </Button>
            </div>
          )}
          <div className="ms-auto flex flex-col items-end gap-1">
            {!canUpload && (
              <span className="text-xs text-muted-foreground">
                An organization admin must enable uploads before you can save.
              </span>
            )}
            <Button
              size="sm"
              disabled={
                busy || !canUpload || !hasChanges || !name.trim() || (mode === "document" && !folder)
              }
              onClick={() => void prepare()}
            >
              {busy ? "Preparing…" : "Review save"}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
